// Package imports
import React, { useRef, useState } from 'react';
import EmailEditor, { EditorRef } from 'react-email-editor';

// CSS imports
import './CreateNewsletterEmail.css';

// Component imports
import TextInput from '../../components/inputs/textInput/TextInput';
import PrimaryButton from '../../components/buttons/primaryButton/PrimaryButton';
import FullPageSpinner from '../../components/fullPageSpinner/FullPageSpinner';
import Alert from '../../components/alert/Alert';

// Helper imports
import SectionWrapper from '../../helpers/components/sectionWrapper/SectionWrapper';

// Service imports
import { ApiService } from '../../services/ApiService';

// Icon imports
import { MdError } from 'react-icons/md';

// Type imports
import { AlertSize } from '../../types/alert/AlertSize';
import { useNavigate } from 'react-router-dom';

const CreateNewsletterEmail: React.FC = () => {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [nameError, setNameError] = useState('');
  const [subjectError, setSubjectError] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [showUnknownError, setShowUnknownError] = useState(false);

  const emailEditorRef = useRef<EditorRef>(null);
  const navigate = useNavigate();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    switch (e.target.name) {
      case 'name': {
        setNameError('');
        setName(e.target.value);
        break;
      }
      case 'subject': {
        setSubjectError('');
        setSubject(e.target.value);
        break;
      }
    }
  };

  const triggerSave = () => {
    const isValid = checkInputs();

    if (isValid) {
      save();
    }
  };

  const save = async () => {
    const unlayer = emailEditorRef.current?.editor;

    unlayer?.exportHtml(async (data) => {
      const { design, html } = data;
      setIsLoading(true);

      const res: string | null = await ApiService.createNewsletterEmail(
        name,
        subject,
        html,
        design
      );

      if (res) {
        navigate('/newsletter-emails');
      } else {
        setShowUnknownError(true);
      }
    });
  };

  const checkInputs = () => {
    let isValid = true;

    if (name === '') {
      setNameError('Please enter a name');
      isValid = false;
    }

    if (subject === '') {
      setSubjectError('Please enter a subject');
      isValid = false;
    }

    return isValid;
  };
  return (
    <div className='CreateNewsletterEmail'>
      <SectionWrapper>
        <div className='content'>
          <h2>Create Email</h2>
          <div className='save-button-container'>
            <PrimaryButton label='Save' onClick={triggerSave} />
          </div>
          <TextInput
            value={name}
            name='name'
            label='Name'
            onChange={onChange}
            errorMessage={nameError}
            className='name-input'
          />
          <TextInput
            value={subject}
            name='subject'
            label='Subject'
            onChange={onChange}
            errorMessage={subjectError}
            className='subject-input'
          />
          <EmailEditor ref={emailEditorRef} />
        </div>
      </SectionWrapper>
      {isLoading ? <FullPageSpinner /> : null}
      <Alert
        showAlert={showUnknownError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowUnknownError}
      >
        <MdError size={20} />
        <span>Something went wrong!</span>
      </Alert>
    </div>
  );
};

export default CreateNewsletterEmail;
